// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import classNames from 'classnames';
import { Text, Heading } from '@nerdwallet/react-typography';

import {
  backgroundColorPicker,
  repeatable,
  textColorPicker,
  titleInput,
  boolCheckboxSpec,
  subtitleInput,
  anchorIdInput,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';

import getValidColor from '../../lib/get-valid-color';

import VerticalSpacer from '../vertical-spacer/vertical-spacer';
import ZigZagSectionStretch from './zig-zag-section-stretch';

import './zig-zag.less';

const titleTextColorPicker = {
  ...textColorPicker,
  fieldName: 'titleTextColor',
  fieldLabel: 'Title text color',
};

const ZigZag = ({
  anchorId,
  backgroundColor,
  titleStyle,
  title,
  titleTextColor,
  subtitle,
  subtitleWidthPercentage,
  sections,
  size,
  imageContentFirst,
}) => {
  const wrapperStyle = {
    backgroundColor: getValidColor(backgroundColor),
  };

  const Sections = sections.map((section) => (
    <ZigZagSectionStretch
      {...section}
      textColor={titleTextColor}
      key={section.header}
      size={size}
      imageContentFirst={imageContentFirst}
    />
  ));

  const subtitleWidthDefault = {
    lg: '960px',
    md: '706px',
    sm: '288px',
  };

  const subtitleWidth =
    subtitleWidthPercentage || `${subtitleWidthDefault[size]}`;

  const subtitleWidthRule = {
    lg: subtitleWidthPercentage ? `${subtitleWidth}%` : `${subtitleWidth}`,
    md: subtitleWidthPercentage ? `${subtitleWidth}%` : `${subtitleWidth}`,
    sm: '288px',
  };

  return (
    <div id={anchorId} className="zig-zag" style={wrapperStyle}>
      <VerticalSpacer mobile="p-30" tablet="p-40" desktop="p-40" />
      <VerticalSpacer tablet="p-40" desktop="p-40" />
      <Heading
        className={classNames(['zig-zag__title', titleStyle])}
        level={2}
        color={titleTextColor || 'neutral-darkest'}
      >
        {title}
      </Heading>
      {subtitle && (
        <VerticalSpacer mobile="p-18" tablet="p-18" desktop="p-18" />
      )}
      {subtitle && (
        <div>
          <Text
            className="zig-zag__subtitle"
            color={titleTextColor}
            style={{
              maxWidth: `${subtitleWidthRule[size]}`,
            }}
          >
            {subtitle}
          </Text>
          <VerticalSpacer tablet="p-40" desktop="p-40" />
        </div>
      )}
      <VerticalSpacer mobile="p-30" tablet="p-40" desktop="p-40" />
      <ul className="zig-zag__sections">{Sections}</ul>
      <VerticalSpacer tablet="p-40" desktop="p-40" />
      <VerticalSpacer tablet="p-40" desktop="p-40" />
    </div>
  );
};

ZigZag.propTypes = {
  anchorId: PropTypes.string,
  title: PropTypes.string,
  titleTextColor: PropTypes.string,
  titleStyle: PropTypes.oneOf([
    'gotham-regular',
    'gotham-black',
    'chronicle-regular',
  ]),
  subtitle: PropTypes.string,
  subtitleWidthPercentage: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      backgroundAlt: PropTypes.string,
      backgroundHeight: PropTypes.number,
      backgroundHeightMobile: PropTypes.number,
      backgroundUrl: PropTypes.string,
      backgroundUrlMobile: PropTypes.string,
      backgroundWidth: PropTypes.number,
      backgroundWidthMobile: PropTypes.number,
      header: PropTypes.string,
      content: PropTypes.string,
      contentMobile: PropTypes.string,
      textColor: PropTypes.string,
    })
  ),
  backgroundColor: PropTypes.string,
  size: PropTypes.oneOf(['', 'sm', 'md', 'lg']),
  imageContentFirst: PropTypes.bool,
};

ZigZag.getEditingConfig = () => [
  backgroundColorPicker,
  titleTextColorPicker,
  stringSelectSpec('titleStyle', 'Title font family', [
    { label: 'Gotham', value: 'gotham-regular' },
    { label: 'Gotham Black', value: 'gotham-black' },
    { label: 'Chronicle', value: 'chronicle-regular' },
  ]),
  titleInput,
  subtitleInput,
  stringInputSpec(
    'subtitleWidthPercentage',
    'Max-width of subtitle (in %, not pixels)'
  ),
  anchorIdInput,
  boolCheckboxSpec(
    'imageContentFirst',
    'First zig-zag element an image? (instead of text content)'
  ),
  repeatable('sections', 'Elements', 'Element', () =>
    ZigZagSectionStretch.getEditingConfig()
  ),
];

ZigZag.defaultProps = {
  size: '',
  sections: [],
  imageContentFirst: false,
  titleStyle: 'gotham',
  titleTextColor: '',
};

ZigZag.displayName = 'ZigZag';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ZigZag);
